import { useCallback, useEffect, useState } from "react";

export type ColorBlindMode = "none" | "protanopia" | "deuteranopia" | "tritanopia" | "achromatopsia";
export type ContrastMode = "none" | "high" | "dark" | "light" | "invert";

export interface A11ySettings {
  // Content
  textScale: number; // 1.0 - 2.0
  lineHeight: number; // 1.0 - 2.4
  letterSpacing: number; // 0 - 0.2 em
  readableFont: boolean;
  dyslexicFont: boolean;
  bigCursor: boolean;
  hideImages: boolean;
  highlightLinks: boolean;
  highlightHeadings: boolean;
  stopAnimations: boolean;
  tooltips: boolean;

  // Color
  contrastMode: ContrastMode;
  saturation: number; // 0 - 200
  brightness: number; // 50 - 150
  colorBlindMode: ColorBlindMode;

  // Navigation
  readingGuide: boolean;
  readingMask: boolean;
  focusOutline: boolean;
}

export const DEFAULT_SETTINGS: A11ySettings = {
  textScale: 1,
  lineHeight: 1.5,
  letterSpacing: 0,
  readableFont: false,
  dyslexicFont: false,
  bigCursor: false,
  hideImages: false,
  highlightLinks: false,
  highlightHeadings: false,
  stopAnimations: false,
  tooltips: false,
  contrastMode: "none",
  saturation: 100,
  brightness: 100,
  colorBlindMode: "none",
  readingGuide: false,
  readingMask: false,
  focusOutline: false,
};

const STORAGE_KEY = "a11y-settings-v1";

function loadSettings(): A11ySettings {
  if (typeof window === "undefined") return DEFAULT_SETTINGS;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    return { ...DEFAULT_SETTINGS, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

function applySettings(s: A11ySettings) {
  const root = document.documentElement;

  // Text scale
  root.style.setProperty("--a11y-text-scale", String(s.textScale));
  root.style.fontSize = `${s.textScale * 100}%`;

  // Line height & spacing
  root.style.setProperty("--a11y-line-height", String(s.lineHeight));
  root.style.setProperty("--a11y-letter-spacing", `${s.letterSpacing}em`);

  const toggle = (cls: string, on: boolean) => root.classList.toggle(cls, on);

  toggle("a11y-line-height", s.lineHeight !== 1.5);
  toggle("a11y-letter-spacing", s.letterSpacing > 0);
  toggle("a11y-readable-font", s.readableFont);
  toggle("a11y-dyslexic-font", s.dyslexicFont);
  toggle("a11y-big-cursor", s.bigCursor);
  toggle("a11y-hide-images", s.hideImages);
  toggle("a11y-highlight-links", s.highlightLinks);
  toggle("a11y-highlight-headings", s.highlightHeadings);
  toggle("a11y-stop-animations", s.stopAnimations);
  toggle("a11y-tooltips", s.tooltips);
  toggle("a11y-focus-outline", s.focusOutline);

  // Contrast modes (mutually exclusive)
  ["a11y-contrast-high", "a11y-contrast-dark", "a11y-contrast-light", "a11y-contrast-invert"].forEach((c) =>
    root.classList.remove(c),
  );
  if (s.contrastMode !== "none") root.classList.add(`a11y-contrast-${s.contrastMode}`);

  // Color blindness (mutually exclusive)
  if (s.colorBlindMode !== "none") {
    root.style.setProperty("--a11y-cb-filter", `url('#a11y-${s.colorBlindMode}')`);
    root.classList.add("a11y-cb");
  } else {
    root.style.setProperty("--a11y-cb-filter", " ");
    root.classList.remove("a11y-cb");
  }

  // Filter (saturation + brightness)
  const needsFilter = s.saturation !== 100 || s.brightness !== 100;
  if (needsFilter) {
    root.style.setProperty(
      "--a11y-filter",
      `saturate(${s.saturation}%) brightness(${s.brightness}%)`,
    );
    root.classList.add("a11y-filter");
  } else {
    root.style.setProperty("--a11y-filter", " ");
    root.classList.remove("a11y-filter");
  }
}

export function useAccessibility() {
  const [settings, setSettings] = useState<A11ySettings>(() => loadSettings());

  useEffect(() => {
    applySettings(settings);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch {
      /* ignore */
    }
  }, [settings]);

  const update = useCallback(<K extends keyof A11ySettings>(key: K, value: A11ySettings[K]) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  }, []);

  const reset = useCallback(() => setSettings(DEFAULT_SETTINGS), []);

  return { settings, update, reset };
}
